import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

export type NoteItem = {
  id: string
  dateTime: string
  user: string
  title: string
  notes: string
}

type NotesDrawerProps = {
  open: boolean
  onClose: () => void
  header?: string
  notes?: NoteItem[]
  onAddNote?: (newNote: { title?: string; notes: string }) => void
}

export default function NotesDrawer({ open, onClose, header = 'Notes', notes, onAddNote }: NotesDrawerProps) {
  const [newNote, setNewNote] = useState('')

  const isSaveDisabled = newNote.trim().length === 0
  const rows = useMemo(() => notes ?? [], [notes])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 'clamp(480px, 50vw, 1200px)',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="16px 12px">
        <Typography variant="subtitle1" fontWeight={600}>
          {header}
        </Typography>

        <IconButton onClick={onClose} size="small" aria-label="Close notes drawer">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />
      <Box padding={2} flex={1} overflow="auto">
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" color="secondary">
            <TableHead>
              <TableRow>
                <TableCell width={120}>Date and Time</TableCell>
                <TableCell width={120}>User</TableCell>
                <TableCell width={120}>Title</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No notes yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(n => (
                  <TableRow key={n.id} hover>
                    <TableCell>{n.dateTime}</TableCell>
                    <TableCell>{n.user}</TableCell>
                    <TableCell>{n.title}</TableCell>
                    <TableCell className="note-cell">{n.notes}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box padding={2} borderTop="1px solid rgba(0,0,0,0.12)" bgcolor="#f5f5f5">
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="Add Note"
          value={newNote}
          onChange={e => setNewNote(e.target.value)}
        />

        <Box display="flex" justifyContent="flex-end" marginTop={1.5}>
          <Button
            variant="contained"
            disabled={isSaveDisabled}
            onClick={() => {
              const trimmed = newNote.trim()
              if (!trimmed) return
              onAddNote?.({ notes: trimmed })
              setNewNote('')
            }}
          >
            SAVE AND ADD NOTE
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
